import { appConfig } from "@/config/app";

/** Metres in one foot, exactly (international foot, 1959). */
const METRES_PER_FOOT = 0.3048;
/** Square feet in one square metre: 10.763910416709722, from the exact foot; areas show m² with sq ft alongside (D-009). */
export const SQ_FT_PER_M2 = 1 / (METRES_PER_FOOT * METRES_PER_FOOT);
const MM2_PER_M2 = 1_000_000;
const MM_PER_M = 1_000;
const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 86_400_000;

const integer = new Intl.NumberFormat(appConfig.locale, { maximumFractionDigits: 0 });
const exact = new Intl.NumberFormat(appConfig.locale, {
  maximumFractionDigits: appConfig.exactFractionDigits,
});
const date = new Intl.DateTimeFormat(appConfig.locale, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: appConfig.timeZone,
});
const shortDate = new Intl.DateTimeFormat(appConfig.locale, {
  day: "numeric",
  month: "short",
  timeZone: appConfig.timeZone,
});
const time = new Intl.DateTimeFormat(appConfig.locale, {
  hour: "numeric",
  minute: "2-digit",
  timeZone: appConfig.timeZone,
});
const relative = new Intl.RelativeTimeFormat(appConfig.locale, { numeric: "auto", style: "short" });

/** An exact measurement with grouping and no padded zeros: 10.40415 stays 10.40415, 1250 becomes "1,250". */
export function formatExact(value: number): string {
  return exact.format(value);
}

/** "1 file", "2 files", "1,200 files" (en-IN grouping). Pass the plural when it isn't the word plus "s". */
export function plural(count: number, one: string, many = `${one}s`): string {
  return `${formatNumber(count)} ${count === 1 ? one : many}`;
}

/** 120000 → "1,20,000" under en-IN. */
export function formatNumber(value: number): string {
  return integer.format(value);
}

/** "20/10/2026" under en-IN. */
export function formatDate(iso: string): string {
  return date.format(new Date(iso));
}

/** "20 Oct". */
export function formatShortDate(iso: string): string {
  return shortDate.format(new Date(iso));
}

/** "4:30 pm". */
export function formatTime(iso: string): string {
  return time.format(new Date(iso));
}

/** Square feet in `squareMetres`, unrounded. */
export function squareFeet(squareMetres: number): number {
  return squareMetres * SQ_FT_PER_M2;
}

/** "1,020 m² (10,979.18862 sq ft)": both values exact, nothing rounded to whole numbers. */
export function formatArea(squareMetres: number): string {
  return `${formatExact(squareMetres)} m² (${formatExact(squareFeet(squareMetres))} sq ft)`;
}

/** `formatArea` from square millimetres. */
export function formatAreaMm2(mm2: number): string {
  return formatArea(mm2 / MM2_PER_M2);
}

/** A length from millimetres, unrounded: "850 mm" below a metre, "4.25 m" from a metre up. */
export function formatLengthMm(mm: number): string {
  return Math.abs(mm) >= MM_PER_M ? `${formatExact(mm / MM_PER_M)} m` : `${formatExact(mm)} mm`;
}

/** Whole days from `now` to `iso` (negative when past). */
export function daysUntil(iso: string, now: number = Date.now()): number {
  return Math.round((new Date(iso).getTime() - now) / MS_PER_DAY);
}

/** "just now", "5 min ago", "3 hr ago", "yesterday", "4 days ago", then a short date. */
export function formatRelative(iso: string, now: number = Date.now()): string {
  const minutes = Math.round((now - new Date(iso).getTime()) / MS_PER_MINUTE);
  if (minutes < 1) return "just now";
  if (minutes < 60) return relative.format(-minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (hours < 24) return relative.format(-hours, "hour");
  const days = Math.round(hours / 24);
  if (days < 7) return relative.format(-days, "day");
  return formatShortDate(iso);
}
